import { RefObject, useCallback } from "react";
import { Aircraft, Ramp } from "../../../../../../types";
import { uuidv4 } from "../../../../../../utils";
import { useReferenceAircraftState } from "../../../RampEditor/stores/useReferenceAircraftState";
import {
  isOnText,
  isPointInShapeOrOnEdge,
  pointToSegmentDistance,
} from "../math";
import { DraggingPoint, DraggingShape, Shape } from "../RampCanvasV2";

/**
 * Check if a point is inside a rotated rectangle (for reference aircraft hit detection)
 */
const isPointInRotatedRect = (
  px: number,
  py: number,
  centerX: number,
  centerY: number,
  halfWidth: number,
  halfHeight: number,
  angle: number
): boolean => {
  // Translate point to origin
  const translatedX = px - centerX;
  const translatedY = py - centerY;

  // Rotate point by negative angle to align with rectangle
  const cosA = Math.cos(-angle);
  const sinA = Math.sin(-angle);
  const rotatedX = translatedX * cosA - translatedY * sinA;
  const rotatedY = translatedX * sinA + translatedY * cosA;

  // Check if point is within axis-aligned rectangle
  return (
    Math.abs(rotatedX) <= halfWidth && Math.abs(rotatedY) <= halfHeight
  );
};

/**
 * Check if click is on the reference aircraft
 */
export const isClickOnReferenceAircraft = (
  x: number, // in pixels
  y: number, // in pixels
  aircraft: Aircraft | null,
  position: { x: number; y: number; angle: number }, // in pixels
  pixelsToFeetRatio: number
): boolean => {
  if (!aircraft || !aircraft.wingspan || !aircraft.length) {
    return false;
  }

  const halfWidth = (aircraft.wingspan * pixelsToFeetRatio) / 2;
  const halfHeight = (aircraft.length * pixelsToFeetRatio) / 2;

  return isPointInRotatedRect(
    x,
    y,
    position.x,
    position.y,
    halfWidth,
    halfHeight,
    position.angle
  );
};

export const clickedShape = (
  canvasRef: RefObject<HTMLCanvasElement>,
  event: React.MouseEvent,
  shapes: Shape[]
): boolean => {
  const rect = canvasRef.current?.getBoundingClientRect();
  if (!rect) {
    return;
  }
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;
  // Check if we're clicking on a point in an existing shape
  for (const shape of shapes) {
    if (shape.locked) {
      continue;
    }

    const pointIndex = shape.points.findIndex(
      (point) => Math.hypot(point.x - x, point.y - y) < 10
    );

    if (pointIndex !== -1) {
      return true;
    }

    if (shape.type === "Text") {
      // since Text is just a single point, just evaluate that point w/ a nice buffer
      // adjust for the length of the text. the user will perceive the shape as being
      // the full length of the text on the screen
      const canvas = canvasRef.current;
      if (isOnText(canvas, shape, { x, y })) {
        return true;
      }
    }
    // check if the mouse is inside the shape
    if (isPointInShapeOrOnEdge({ x, y }, shape.points)) {
      return true;
    }
    // Check if the mouse is close to any line segment of the shape
    for (let j = 0; j < shape.points.length; j++) {
      const start = shape.points[j];
      const end = shape.points[(j + 1) % shape.points.length];
      const distToLine = pointToSegmentDistance({ x, y }, start, end);

      if (distToLine < 10) {
        // Threshold to detect the line
        return true;
      }
    }
  }
  return false;
};

// Start dragging a point (either from existing shapes or the current shape)
type Props = {
  selectedTool: string;
  setSelectedTool: (tool: string) => void;
  shapes: Shape[];
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  currentShape: Shape;
  setDraggingPoint: React.Dispatch<React.SetStateAction<DraggingPoint>>;
  setDraggingShape: React.Dispatch<React.SetStateAction<DraggingShape>>;
  draggingPoint: DraggingPoint;
  draggingShape: DraggingShape;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  ramp: Ramp;
  setHistory: React.Dispatch<React.SetStateAction<Shape[][]>>;
  setTextPrompt: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      x: number;
      y: number;
    }>
  >;
  pixelsToFeetRatio: number;
  setActiveShapes: React.Dispatch<React.SetStateAction<string[]>>;
  activeShapes: string[];
};

export const useOnMouseDown = (props: Props) => {
  const {
    selectedTool,
    setSelectedTool,
    shapes,
    setShapes,
    currentShape,
    setDraggingPoint,
    setDraggingShape,
    canvasRef,
    ramp,
    setHistory,
    setTextPrompt,
    pixelsToFeetRatio,
    setActiveShapes,
    activeShapes,
  } = props;

  const {
    aircraft: referenceAircraft,
    position: referencePosition,
    setIsSelected: setReferenceIsSelected,
    setIsDragging: setReferenceIsDragging,
  } = useReferenceAircraftState();

  return useCallback(
    (event: React.MouseEvent) => {
      if (selectedTool === "Measuring") {
        return;
      }

      // Only handle left-click (button 0), ignore right-click (button 2) and middle-click (button 1)
      if (event.button !== 0) {
        return;
      }

      setHistory((previousHistory) => {
        // check if the md5 of the last shape is the same as the current shape
        // if it is, don't add it to the history
        if (
          previousHistory.length > 0 &&
          JSON.stringify(previousHistory[previousHistory.length - 1]) ===
            JSON.stringify(shapes)
        ) {
          return previousHistory;
        }
        return [...previousHistory, shapes];
      });

      const canvas = canvasRef.current;
      const rect = canvas?.getBoundingClientRect();
      if (!rect || !canvas) {
        return;
      }

      // Scale mouse coordinates to match canvas internal resolution
      // This handles cases where canvas.width differs from CSS display width (zoom, DPR, etc.)
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (event.clientX - rect.left) * scaleX;
      const y = (event.clientY - rect.top) * scaleY;

      // Check if clicking on reference aircraft first
      if (referenceAircraft) {
        const refPositionPixels = {
          x: referencePosition.x * pixelsToFeetRatio,
          y: referencePosition.y * pixelsToFeetRatio,
          angle: referencePosition.angle,
        };
        if (
          isClickOnReferenceAircraft(
            x,
            y,
            referenceAircraft,
            refPositionPixels,
            pixelsToFeetRatio
          )
        ) {
          setReferenceIsSelected(true);
          setReferenceIsDragging(true);
          return;
        } else {
          // Clicked elsewhere, deselect reference aircraft
          setReferenceIsSelected(false);
          setReferenceIsDragging(false);
        }
      }

      // if we're in Text mode, add a text pill
      if (selectedTool === "Text") {
        setTextPrompt({
          open: true,
          x,
          y,
        });
        return;
        const text = prompt("Enter text");
        if (text) {
          const updatedShapes = [...shapes];
          updatedShapes.unshift({
            id: uuidv4(),
            fbo_id: ramp.fbo_id,
            ramp_id: ramp.id,
            tags: [],
            points: [{ x, y }],
            locked: false,
            type: "Text",
            stroke_style: "white",
            line_style: "solid",
            line_width: 3,
            text,
            font_size: 18,
          });
          setShapes(updatedShapes);
          setSelectedTool("Select");
          return;
        }
      }

      // Check if we're clicking on a point in an existing shape
      // Iterate backwards to check frontmost shapes first (later in array = higher z-index)
      for (let i = shapes.length - 1; i >= 0; i--) {
        const shape = shapes[i];
        if (shape.locked) {
          continue;
        }

        const pointIndex = shape.points.findIndex(
          (point) => Math.hypot(point.x - x, point.y - y) < 10
        );

        if (pointIndex !== -1) {
          // Prevent dragging corner points for Spots (fixed size)
          if (shape.type === "Spot") {
            continue;
          }
          // Only select if not already selected AND no modifier keys pressed
          // (Shift/Cmd+click is handled by useOnCanvasClick for multi-select)
          if (!activeShapes.includes(shape.id) && !event.shiftKey && !event.metaKey && !event.ctrlKey) {
            setActiveShapes([shape.id]);
          }
          setDraggingPoint({ shapeId: shape.id, pointIndex });
          return;
        }

        if (shape.type === "Text") {
          // since Text is just a single point, just evaluate that point w/ a nice buffer
          // adjust for the length of the text. the user will perceive the shape as being
          // the full length of the text on the screen
          const canvas = canvasRef.current;
          if (isOnText(canvas, shape, { x, y })) {
            // Only select if not already selected AND no modifier keys pressed
            // (Shift/Cmd+click is handled by useOnCanvasClick for multi-select)
            if (!activeShapes.includes(shape.id) && !event.shiftKey && !event.metaKey && !event.ctrlKey) {
              setActiveShapes([shape.id]);
            }
            setDraggingShape({ shapeId: shape.id, offset: { x, y } });
            return;
          }
        }
        // check if the mouse is inside the shape
        if (isPointInShapeOrOnEdge({ x, y }, shape.points)) {
          // Only select if not already selected AND no modifier keys pressed
          // (Shift/Cmd+click is handled by useOnCanvasClick for multi-select)
          if (!activeShapes.includes(shape.id) && !event.shiftKey && !event.metaKey && !event.ctrlKey) {
            setActiveShapes([shape.id]);
          }
          setDraggingShape({ shapeId: shape.id, offset: { x, y } });
          return;
        }
        // Check if the mouse is close to any line segment of the shape
        for (let j = 0; j < shape.points.length; j++) {
          const start = shape.points[j];
          const end = shape.points[(j + 1) % shape.points.length];
          const distToLine = pointToSegmentDistance({ x, y }, start, end);

          if (distToLine < 10) {
            // Threshold to detect the line
            // Only select if not already selected AND no modifier keys pressed
            // (Shift/Cmd+click is handled by useOnCanvasClick for multi-select)
            if (!activeShapes.includes(shape.id) && !event.shiftKey && !event.metaKey && !event.ctrlKey) {
              setActiveShapes([shape.id]);
            }
            setDraggingShape({ shapeId: shape.id, offset: { x, y } });
            return;
          }
        }
      }

      // Check if we're clicking on a point in the current shape
      const pointIndex = currentShape.points.findIndex(
        (point) => Math.hypot(point.x - x, point.y - y) < 10
      );

      if (pointIndex !== -1) {
        setDraggingPoint({ shapeId: null, pointIndex });
      }
    },
    [
      props,
      referenceAircraft,
      referencePosition,
      pixelsToFeetRatio,
      setReferenceIsSelected,
      setReferenceIsDragging,
    ]
  );
};
